import type { MenuItem } from '../Menu';

/**
 * How much gutter a row's quick actions need, now that each one is a LABELLED button.
 *
 * It is here rather than in `RowQuickActions.tsx` for the reason `buttonSurface.ts` and
 * `indexTableSurface.ts` are their own files: a module that exports both a component and a
 * constant loses fast refresh for everything in it, and the linter says so.
 *
 * **Why this stopped being a constant.** It was `QUICK_ACTION_WIDTH = 32` — one number per
 * slot, because every quick action was the same 28px square glyph. Product call, 09 Oct 2026:
 * a quick action is a `<Button>` with its name on it, so two actions on one row are no longer
 * the same width and a per-slot constant cannot describe them. The table is
 * `table-layout: fixed` with an explicit `<colgroup>`, so the gutter is exactly as wide as it
 * is told — under-wide is a button that is drawn, clipped, and cannot be pressed.
 *
 * **It errs WIDE, deliberately**, and that is the same call the slot count already made: it
 * measures every entry, including ones `<RowQuickActions>` will drop for want of an icon or a
 * permission, so a reader lacking a key gets a slightly wide gutter rather than a clipped
 * button. Over-wide is invisible; under-wide is broken.
 */

/**
 * How a row's quick actions are drawn. `label` — a `<Button size="sm">` wearing its name — is
 * the rule. `icon` is the ONE exception, **Repository only** (product call, 13 Oct 2026): a
 * file browser's star and download are read as glyphs, as GRC's Documents workspace draws them.
 */
export type QuickActionStyle = 'label' | 'icon';

/** An icon quick action is a fixed 32px square — the `sm` step, so the row height is unchanged. */
const ICON_WIDTH = 32;

/** `gap-1` between two buttons, and once more to hold them off the kebab. */
const GAP = 4;

/**
 * Everything the button draws around the label: `px-3` either side at the `sm` step, and
 * nothing else.
 *
 * It has moved twice in one day and both moves are the same lesson. It was 56 while the button
 * carried a leading icon (32 padding + 16 glyph + the `gap-2` between them); the icon came off
 * and it fell to 32; the button then went to `BUTTON_SIZES.sm`, whose `px-3` is 12 a side, and
 * it fell to 24. **A quick action that changes its size step or grows a leading element owes
 * this number the difference.**
 */
const BUTTON_CHROME = 24;

/**
 * A generous per-character width for `text-sm`.
 *
 * **MEASURED, not guessed — and the first guess was wrong.** This was 7.5 on the reasoning
 * that Geist at 14px averages nearer 7px. Rendered on the Repository index it was 8.0 for
 * `Pin` (80px wide) and **8.875 for `Download` (127px)**, so the estimate was 11px UNDER on an
 * eight-character label — which in a `table-layout: fixed` gutter is a button that is drawn
 * and clipped, the one failure this number exists to prevent.
 *
 * 9 was the measured worst case at `text-sm` (14px), rounded up. The button then moved to the
 * `sm` step, whose label is `text-xs` — 13px on this product's +1px scale — so the same worst
 * case scales to 9 × 13/14 ≈ 8.36. Held at **8.5**: still above the measured ratio, and the
 * rounding stays in the direction that is invisible rather than broken.
 *
 * Capitals and round letters are what pull the average above the nominal advance, so a label
 * of them costs more than its character count suggests. **Re-measure before lowering it, and
 * re-measure whenever the size step changes** — over-wide is invisible and under-wide is a
 * button that is drawn and clipped.
 */
const CHAR_WIDTH = 8.5;

/** The gutter one row's quick actions need, in pixels. Zero when there are none. */
export function quickActionsWidth(items: MenuItem[], style: QuickActionStyle = 'label'): number {
  if (items.length === 0) {
    return 0;
  }

  if (style === 'icon') {
    return items.length * (ICON_WIDTH + GAP);
  }

  const buttons = items.reduce(
    (sum, item) => sum + BUTTON_CHROME + Math.ceil(labelOf(item).length * CHAR_WIDTH),
    0,
  );

  return buttons + items.length * GAP;
}

/**
 * A `MenuItem`'s label is typed as a `ReactNode`, and only a string can be measured. Anything
 * else is costed at a plausible word so the gutter is still roughly right rather than zero —
 * a quick action labelled with an element is not a shape this product uses today.
 */
function labelOf(item: MenuItem): string {
  return typeof item.label === 'string' ? item.label : 'Action';
}
